import { supabase } from '@/integrations/supabase/client';
import { Tables } from '@/integrations/supabase/types';
import { AuthError } from '@supabase/supabase-js';

type Profile = Tables<'profiles'>;
type AuthResult = { error: AuthError | Error | null };

/**
 * Signs up a new user with email and password
 */
export async function signUp(
  email: string, 
  password: string, 
  metadata?: { first_name?: string; last_name?: string; role?: string }
): Promise<AuthResult> {
  try {
    // Get the current origin for redirection - make sure we're using the current window location
    // not hardcoded localhost
    const currentOrigin = window.location.origin;
    const redirectUrl = `${currentOrigin}/auth/verification-success`;
    
    // Attempt to sign up the user
    // If the user already exists, Supabase will handle it gracefully
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: metadata,
        emailRedirectTo: redirectUrl
      }
    });
    
    // Profile creation is now handled automatically by database trigger
    // See: supabase/migrations/create_profile_trigger.sql
    
    return { error };
  } catch (error) {
    console.error('Network or connection error during signup:', error);
    return { error: error instanceof Error ? error : new Error('Erreur de connexion lors de l\'inscription') };
  }
}

/**
 * Signs in a user with email and password
 */
export async function signIn(email: string, password: string): Promise<AuthResult> {
  try {
    console.log('Attempting to sign in with email:', email);
    
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password
    });

    if (error) {
      console.error('Sign in error:', error);
    }

    return { error };
  } catch (error) {
    console.error('Network or connection error during sign in:', error);
    
    // Gestion spécifique des erreurs de réseau/certificat
    if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
      return { 
        error: new Error('Erreur de connexion au serveur. Vérifiez votre connexion internet ou essayez de vider le cache de votre navigateur.') 
      };
    }
    
    return { error: error instanceof Error ? error : new Error('Erreur de connexion lors de la connexion') };
  }
}

/**
 * Signs out the current user
 */
export async function signOut(): Promise<AuthResult> {
  try {
    const { error } = await supabase.auth.signOut();
    return { error };
  } catch (error) {
    return { error: error instanceof Error ? error : new Error('Unknown error during sign out') };
  }
}

/**
 * Sends a password reset email to the specified email address
 */
export async function resetPassword(email: string): Promise<AuthResult> {
  try {
    const currentOrigin = window.location.origin;
    
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${currentOrigin}/auth/reset-password`,
    });
    return { error };
  } catch (error) {
    return { error: error instanceof Error ? error : new Error('Unknown error during password reset') };
  }
}

/**
 * Updates the user's password when they have a valid recovery token
 */
export async function updatePassword(newPassword: string): Promise<AuthResult> {
  try {
    const { error } = await supabase.auth.updateUser({
      password: newPassword
    });
    return { error };
  } catch (error) {
    return { error: error instanceof Error ? error : new Error('Unknown error during password update') };
  }
}

/**
 * Formats profile data for update
 */
function formatProfileData(data: Partial<Profile>): Partial<Profile> {
  if (!data.updated_at) return data;
  
  return {
    ...data,
    updated_at: typeof data.updated_at === 'object' 
      ? (data.updated_at as Date).toISOString() 
      : data.updated_at
  };
}

/**
 * Updates a user's profile data
 */
export async function updateUserProfile(userId: string, data: Partial<Profile>): Promise<AuthResult> {
  try {
    if (!userId) {
      return { error: new Error("Utilisateur non connecté") };
    }

    const formattedData = formatProfileData(data);

    // Vérifier si le profil existe déjà
    const { data: existingProfile, error: fetchError } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (fetchError && fetchError.code !== 'PGRST116') { // PGRST116 = not found
      return { error: fetchError };
    }

    // Si le profil n'existe pas, créer un nouveau profil
    if (!existingProfile) {
      const { error } = await supabase
        .from('profiles')
        .insert([{
          id: userId,
          ...formattedData,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }]);

      if (error) {
        return { error };
      }
    } else {
      // Mise à jour du profil existant
      const { error } = await supabase
        .from('profiles')
        .update(formattedData)
        .eq('id', userId);

      if (error) {
        return { error };
      }
    }
    
    return { error: null };
  } catch (error) {
    return { error: error instanceof Error ? error : new Error('Unknown error updating profile') };
  }
}

/**
 * Fetches a user's profile data
 */
export async function fetchUserProfile(userId: string) {
  try {
    if (!userId) {
      return { data: null, error: new Error("Utilisateur non connecté") };
    }

    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();

    return { data, error };
  } catch (error) {
    return { 
      data: null, 
      error: error instanceof Error ? error : new Error('Unknown error fetching profile') 
    };
  }
}
